import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDynamicReducer } from '@micro-frontend-react/employee-experience/lib/useDynamicReducer';

import {
  pbtNotificationsReducer,
  pbtNotificationsReducerName,
  pbtNotificationsInitialState,
} from '../../../Redux/PBT/Notifications/reducers';
import { requestNotificationsSagas } from '../../../Services/NotificationService';
import {
  requestNotifications,
  markNotificationReadRequest,
  markAllNotificationsReadRequest,
} from '../../../Redux/PBT/Notifications/actions';
import type { IPBTNotificationsState } from '../../../Redux/PBT/Notifications/types';

// Poll interval for refreshing notifications (ms).
export const NOTIFICATION_POLL_INTERVAL_MS = 60000;

export interface IUseNotificationsResult {
  notifications: IPBTNotificationsState['notifications'];
  unreadCount: number;
  isLoading: boolean;
  hasError: boolean;
  errorMessage: string | null;
  refresh: () => void;
  markRead: (notificationId: number) => void;
  markAllRead: () => void;
}

export function useNotifications(pollIntervalMs: number = NOTIFICATION_POLL_INTERVAL_MS): IUseNotificationsResult {
  const dispatch = useDispatch();

  useDynamicReducer(pbtNotificationsReducerName, pbtNotificationsReducer, [requestNotificationsSagas]);

  const state = useSelector(
    (s: { dynamic?: Record<string, unknown> }) =>
      (s?.dynamic?.[pbtNotificationsReducerName] as IPBTNotificationsState | undefined) ?? pbtNotificationsInitialState
  );

  const refresh = React.useCallback(() => {
    dispatch(requestNotifications());
  }, [dispatch]);

  const markRead = React.useCallback(
    (notificationId: number) => {
      dispatch(markNotificationReadRequest(notificationId));
    },
    [dispatch]
  );

  const markAllRead = React.useCallback(() => {
    dispatch(markAllNotificationsReadRequest());
  }, [dispatch]);

  React.useEffect(() => {
    refresh();
    if (!pollIntervalMs || pollIntervalMs <= 0) {
      return undefined;
    }
    const intervalId = setInterval(refresh, pollIntervalMs);
    return () => clearInterval(intervalId);
  }, [refresh, pollIntervalMs]);

  return {
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    isLoading: state.isLoading,
    hasError: state.hasError,
    errorMessage: state.errorMessage,
    refresh,
    markRead,
    markAllRead,
  };
}
